/**
 * AB#22394 — every row of an uploaded roster template is checked here, whatever the Excel
 * sheet allowed. The template's dropdowns (product, size, whole-number quantity) are easy to
 * bypass: pasted values, Google Sheets or Numbers, an old copy of the template. So the file is
 * read only when every row names a product of this merchant and one of that product's sizes.
 *
 * Size matching is trimmed and case-insensitive, as in obsoleteSizes / RosterSizeReconciler,
 * and the row is rewritten with the product's own size name so the rest of the form (size_index,
 * pricing) sees the canonical label. The backend refuses a wrong size again on create
 * (ManualOrderSafeguards), so a bypassed form cannot save one either.
 */

export const CUSTOM_PRODUCT = 'Custom Product';

/** The size list the template offers a Custom Product (ProductRoasterExcelExport). */
export const CUSTOM_PRODUCT_SIZES = [
  'One Size',
  'Not applicable',
  'To be entered',
  'YXS',
  'YS',
  'YM',
  'YL',
  'YXL',
  'Y2XL',
  'Adult XS',
  'Adult S',
  'Adult M',
  'Adult L',
  'Adult XL',
  'Adult 2XL',
  'Adult 3XL',
  'Adult 4XL',
  'Adult 5XL',
];

/** Column indexes resolved from the header row; -1 when the column is absent. */
export interface RosterColumns {
  product: number;
  name: number;
  number: number;
  size: number;
  captainAlternate: number;
  quantity: number;
}

/** The part of a catalogue product this check reads. */
export interface RosterProduct {
  sizes?: { json_data?: { name?: unknown }[] | null }[] | null;
}

export interface RosterRowProblem {
  /** The row number as Excel shows it (the header is row 1). */
  row: number;
  product: string;
  message: string;
}

export interface RosterUploadCheck {
  problems: RosterRowProblem[];
  /** The sheet with product and size cells rewritten to their canonical names. */
  rows: unknown[][];
}

const text = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim();

const normalize = (value: unknown): string => text(value).toLowerCase();

/** Two size labels name the same size: trimmed, case-insensitive. */
export function sameSizeName(a: unknown, b: unknown): boolean {
  return normalize(a) === normalize(b);
}

/** The size names of a catalogue product, trimmed, empties dropped. */
export function productSizeNames(product: RosterProduct | null | undefined): string[] {
  const list = product?.sizes?.[0]?.json_data ?? [];
  return list.map((entry) => text(entry?.name)).filter((name) => name !== '');
}

function isWholeQuantity(value: unknown): boolean {
  if (typeof value === 'number') return Number.isInteger(value) && value > 0;
  const raw = text(value);
  return /^\d+$/.test(raw) && parseInt(raw, 10) > 0;
}

/** Lines for the alert: the first few problems, then a count of the rest. */
export function describeRosterProblems(problems: RosterRowProblem[], limit = 8): string[] {
  const lines = problems
    .slice(0, limit)
    .map((p) => `Row ${p.row}${p.product ? ` · ${p.product}` : ''} · ${p.message}`);
  if (problems.length > limit) {
    lines.push(`…and ${problems.length - limit} more.`);
  }
  return lines;
}

/**
 * Check every row of the sheet (`rows[0]` is the header). A row is blank — and skipped — when it
 * has no name, number, size, captain or quantity: the template pre-fills PRODUCTS in row 2.
 */
export function checkRosterUpload(
  rows: unknown[][],
  col: RosterColumns,
  findProduct: (sku: string) => RosterProduct | null | undefined
): RosterUploadCheck {
  const problems: RosterRowProblem[] = [];
  const out: unknown[][] = rows.map((row) => [...(row ?? [])]);
  const cell = (row: unknown[], idx: number) => (idx === -1 ? null : row[idx]);
  const content = [col.name, col.number, col.size, col.captainAlternate, col.quantity];

  for (let i = 1; i < out.length; i++) {
    const row = out[i];
    if (content.every((idx) => text(cell(row, idx)) === '')) continue;

    const excelRow = i + 1;
    const productLabel = text(cell(row, col.product));
    const size = text(cell(row, col.size));
    const quantity = cell(row, col.quantity);
    const add = (message: string) =>
      problems.push({ row: excelRow, product: productLabel, message });

    if (productLabel === '') {
      add('PRODUCTS is empty — pick a product.');
      continue;
    }

    const isCustom = normalize(productLabel) === normalize(CUSTOM_PRODUCT);
    let validSizes: string[];
    if (isCustom) {
      row[col.product] = CUSTOM_PRODUCT;
      validSizes = CUSTOM_PRODUCT_SIZES;
    } else {
      const product = findProduct(productLabel);
      if (!product) {
        add(`'${productLabel}' is not a product of this merchant.`);
        continue;
      }
      validSizes = productSizeNames(product);
    }

    // A custom product may leave size and quantity to be entered later, as before.
    if (size === '') {
      if (!isCustom) add('SIZE* is empty.');
    } else if (validSizes.length > 0) {
      const match = validSizes.find((name) => normalize(name) === normalize(size));
      if (match === undefined) {
        add(`'${size}' is not a size of this product (${validSizes.join(', ')}).`);
      } else {
        row[col.size] = match;
      }
    }

    if (text(quantity) === '') {
      if (!isCustom) add('QUANTITY* is empty.');
    } else if (!isWholeQuantity(quantity)) {
      add(`QUANTITY* must be a whole number above 0 ('${text(quantity)}').`);
    }
  }

  return { problems, rows: out };
}
